// @flow
import cardValidator from 'card-validator';
import moment from 'moment';
import { t } from '@lingui/macro';
import type { Untranslated } from '@rover/shared/js/types';
import { getWordCount } from '@rover/utilities/wordCount';

export const required = (message: Untranslated = t`This field is required.`) => (prop: any) => {
  if (prop === 0 || prop === false) return null;
  if (typeof prop === 'string' && !prop.trim()) return message;

  return prop ? null : message;
};

export const truthy = (message: Untranslated = t`This field must be truthy.`) => (prop: any) =>
  prop ? null : message;

export const notVisible = (message: Untranslated = t`Submit or cancel this form.`) => (prop: any) =>
  prop === false ? null : message;

export const minValue = (
  value: number,
  message: Untranslated = t`This field must be at least ${value}.`
) => (prop: any) => {
  if (!prop && prop !== 0) return message;
  return Number(prop) >= value ? null : message;
};

export const maxValue = (
  value: number,
  message: Untranslated = t`This field must be no more than ${value}.`
) => (prop: any) => {
  if (!prop && prop !== 0) return message;
  return Number(prop) <= value ? null : message;
};

export const minLength = (
  length: number = 10,
  message: Untranslated = t`This field must be at least ${length} characters long.`
) => (prop: any) => {
  if (!prop) return message;
  return prop.length >= length ? null : message;
};

export const maxLength = (
  length: number,
  message: Untranslated = t`This field must be at most ${length} characters long.`
) => (prop: ?string) => {
  if (typeof prop !== 'string') return message;

  return prop.length <= length ? null : message;
};

export const withinInclusiveRange = (
  min: number,
  max: number,
  message: Untranslated = t`This field must be between ${min} and ${max}.`
) => (prop: ?number) => {
  if (typeof prop !== 'number') return message;

  const meetsMin = min <= prop;
  const meetsMax = max >= prop;

  return meetsMin && meetsMax ? null : message;
};

export const minWordCount = (
  length: number,
  message: Untranslated = t`Please write at least ${length} words.`
) => (prop: ?string): ?string => {
  if (typeof prop !== 'string') return message;

  const wordCount = getWordCount(prop);
  const meetsWordCount = typeof wordCount === 'number' && wordCount >= length;

  return meetsWordCount ? null : message;
};

export const zipcode = (regex: ?RegExp, message: Untranslated = t`Enter a valid zipcode.`) => (
  prop: any
) => {
  if (!prop) return message;
  const zipcodeRegex =
    regex || /(^\d{5}(-\d{4})?$)|(^[ABCEGHJKLMNPRSTVXY]{1}\d{1}[A-Z]{1} *\d{1}[A-Z]{1}\d{1}$)/;
  return zipcodeRegex.test(prop.trim()) ? null : message;
};

export const notEmptyArray = (message: Untranslated = t`This field is required.`) => (prop: any) =>
  Array.isArray(prop) && prop.length > 0 ? null : message;

export const date = (
  pattern: string,
  message: string = `Enter a valid date${pattern ? ` (${pattern})` : ''}`
) => (prop: any) => {
  if (!prop) {
    return message;
  }
  if (pattern) {
    return moment(prop, pattern, true).isValid() ? null : message;
  }
  return moment(prop).isValid() ? null : message;
};

export const cardHolderName = (
  message: Untranslated = t`We cannot store names with only numbers in our system.`
) => (prop: any) => (/^[\d\s-]+$/.test(prop) ? message : null);

export const creditCardNumber = (message: string = 'Enter a valid credit card number.') => (
  prop: any
) => (cardValidator.number(prop).isValid ? null : message);

export const creditCardExpiration = (message: string = 'Enter a valid expiration date.') => (
  prop: any
) => (cardValidator.expirationDate(prop).isValid ? null : message);

export const creditCardCVV = (number: number, message: string = 'Enter a valid CVV.') => (
  prop: any
) => {
  if (number) {
    const card = cardValidator.number(number);
    const cvvLength = card.card && card.card.code && card.card.code.size;
    return cardValidator.cvv(prop, cvvLength).isValid ? null : message;
  }
  return cardValidator.cvv(prop).isValid ? null : message;
};

export const noUndefinedTimes = (message: Untranslated = t`Please select a time.`) => (
  days: any
) => {
  const errors = [];
  let isError = false;
  days.forEach(day => {
    const dayErrors = [];
    day.times.forEach(time => {
      if (!time.value) {
        isError = true;
        dayErrors.push(message);
      } else {
        dayErrors.push('');
      }
    });
    errors.push(dayErrors);
  });
  return isError ? errors : null;
};

export const maxNumberOfTimesPerDay = (
  value: number,
  message: Untranslated = t`You can only schedule up to ${value} times per day.`
) => (days: any) => {
  const errors = [];
  let isError = false;
  days.forEach(day => {
    const dayErrors = [];
    if (Array.isArray(day.times) && day.times.length > value) {
      isError = true;
      dayErrors.push(message);
    } else {
      dayErrors.push('');
    }
    errors.push(dayErrors);
  });
  return isError ? errors : null;
};

export const requiredFuzzyTime = (
  message: Untranslated = t`Please select a start and end time.`
) => (fuzzyTime: any) =>
  fuzzyTime &&
  fuzzyTime.earliest &&
  fuzzyTime.earliest.value &&
  fuzzyTime.latest &&
  fuzzyTime.latest.value
    ? null
    : message;



// WEBPACK FOOTER //
// ../react-lib/src/components/utils/Form/validation/validators.js